import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import {
  ADD_ASC_PROJECTS_FILE,
  AddAscProjectsFileMutation,
  AddAscProjectsFileMutationVariables,
  GET_ASC_PROJECTS_FILE_SIGNED_UPLOAD_URL,
  GET_BACKGROUND_OPERATIONS,
  GET_COMPANY,
  GET_ONE_SCHEMA_JWT,
  GetAscProjectsFileSignedUploadUrlMutation,
  GetAscProjectsFileSignedUploadUrlMutationVariables,
  GetBackgroundOperationsQueryVariables,
  GetCompanyQueryVariables,
  GetOneSchemaJwtMutation,
  GetOneSchemaJwtMutationVariables,
  useCurrentUser,
} from '@neo.tax/shared';

type UseAddAscProjectsFileParams = {
  companyId: string;
  isOpen: boolean;
};

type UseAddAscProjectsFileReturn = {
  handleSuccess: () => Promise<void>;
  isLoading: boolean;
  oneSchemaJwt: string | null;
  permanentUrl: string | null;
  signedUrl: string | null;
};

const BULK_UPLOAD_FILE_NAME = 'asc-projects-bulk-create.csv';

export const useAddAscProjectsFile = ({
  companyId,
  isOpen,
}: UseAddAscProjectsFileParams): UseAddAscProjectsFileReturn => {
  const { user } = useCurrentUser();
  const [getOneSchemaJwt, { data: oneSchemaJwtData }] = useMutation<
    GetOneSchemaJwtMutation,
    GetOneSchemaJwtMutationVariables
  >(GET_ONE_SCHEMA_JWT);
  const [getUploadUrlMutation, { data: uploadUrlData, loading: isLoading }] = useMutation<
    GetAscProjectsFileSignedUploadUrlMutation,
    GetAscProjectsFileSignedUploadUrlMutationVariables
  >(GET_ASC_PROJECTS_FILE_SIGNED_UPLOAD_URL);
  const [addAscProjectsFile] = useMutation<AddAscProjectsFileMutation, AddAscProjectsFileMutationVariables>(
    ADD_ASC_PROJECTS_FILE,
  );

  useEffect(() => {
    const initOneSchemaJwt = async (): Promise<void> => {
      if (!isOpen || !user?.id) {
        return;
      }
      await getOneSchemaJwt({
        variables: { userId: user.id },
        context: {
          errorToast: { title: 'Error preparing bulk upload' },
        },
      });
    };
    initOneSchemaJwt();
  }, [getOneSchemaJwt, isOpen, user?.id]);

  useEffect(() => {
    const initUploadUrls = async (): Promise<void> => {
      if (!isOpen) {
        return;
      }
      await getUploadUrlMutation({
        variables: { companyId, fileName: BULK_UPLOAD_FILE_NAME },
        context: {
          errorToast: { title: 'Error preparing bulk upload' },
        },
      });
    };
    initUploadUrls();
  }, [companyId, getUploadUrlMutation, isOpen]);

  const handleSuccess = useCallback(async (): Promise<void> => {
    const permanentUrl = uploadUrlData?.getAscProjectsFileSignedUploadUrl.permanentUrl;
    if (!permanentUrl) {
      return;
    }
    await addAscProjectsFile({
      variables: {
        companyId,
        input: {
          permanentUrl,
        },
      },
      refetchQueries: [
        {
          query: GET_COMPANY,
          variables: { companyId } satisfies GetCompanyQueryVariables,
        },
        {
          query: GET_BACKGROUND_OPERATIONS,
          variables: { companyId } satisfies GetBackgroundOperationsQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
      context: {
        successToast: {
          title: 'Bulk upload started!',
          description: "We're creating your projects. They'll appear on this page once processing completes.",
        },
        errorToast: { title: 'Failed to start bulk upload' },
      },
    });
  }, [companyId, addAscProjectsFile, uploadUrlData]);

  return {
    handleSuccess,
    isLoading,
    oneSchemaJwt: oneSchemaJwtData?.getOneSchemaJwt ?? null,
    permanentUrl: uploadUrlData?.getAscProjectsFileSignedUploadUrl.permanentUrl ?? null,
    signedUrl: uploadUrlData?.getAscProjectsFileSignedUploadUrl.signedUrl ?? null,
  };
};
